"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  TablePagination,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material"
import { Assignment, Search, CheckCircle, Schedule, Cancel, Person, TrendingUp } from "@mui/icons-material"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BonafideSection = ({ data = [], period = 'yearly' }) => {
  const [bonafideRequests, setBonafideRequests] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  // Use prop data if available, otherwise fetch from API
  useEffect(() => {
    if (data && data.length > 0) {
      const mappedRequests = data.map((req) => ({
        id: req.id,
        employeeName: req.name || req.employeeName,
        employeeId: req.employee_id || req.employeeId,
        purpose: req.reason || req.purpose,
        status: req.status || "Pending",
        requestDate: req.created_at || req.createdAt || new Date().toISOString(),
        processedDate: req.updated_at || req.updatedAt,
        department: req.department,
        candidateEmail: req.candidateEmail,
      }))
      setBonafideRequests(mappedRequests)
      setIsLoading(false)
    } else {
      fetchBonafideData()
    }
  }, [data])

  const fetchBonafideData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      setIsLoading(true)
      const response = await axios.get(`${API_URL}/api/employee-report/summary?period=${period}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      
      if (response.data.status === "success" && response.data.data) {
        const bonafideData = response.data.data.bonafideRequests || [];
        const mappedRequests = bonafideData.map((req) => ({
          id: req.id,
          employeeName: req.name || req.employeeName,
          employeeId: req.employee_id || req.employeeId,
          purpose: req.reason || req.purpose,
          status: req.status || "Pending",
          requestDate: req.created_at || req.createdAt || new Date().toISOString(),
          processedDate: req.updated_at || req.updatedAt,
          department: req.department,
          candidateEmail: req.candidateEmail,
        }))
        setBonafideRequests(mappedRequests)
      } else {
        throw new Error("Failed to load bonafide requests")
      }
    } catch (err) {
      setError("Failed to fetch bonafide requests. Please try again.")
      //console.error("Error fetching bonafide data:", err)
    } finally {
      setIsLoading(false)
    }
  }

  // Filter to show only pending requests
  const pendingRequests = bonafideRequests.filter((request) => 
    request.status?.toLowerCase() === "pending"
  )

  // Filter pending requests based on search
  const filteredRequests = pendingRequests.filter((request) => {
    const matchesSearch =
      request.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.department?.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })

  // Pagination
  const paginatedRequests = filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Status chip styling
  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase() || "pending"
    const statusConfig = {
      approved: { color: "success", icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      pending: { color: "warning", icon: <Schedule sx={{ fontSize: 16 }} /> },
      rejected: { color: "error", icon: <Cancel sx={{ fontSize: 16 }} /> },
      cancelled: { color: "default", icon: <Cancel sx={{ fontSize: 16 }} /> },
    }

    const config = statusConfig[statusLower] || statusConfig.pending

    return (
      <Chip 
        label={status || "Pending"} 
        color={config.color} 
        size="small" 
        icon={config.icon} 
        sx={{ fontWeight: 500 }} 
      />
    )
  }

  // Statistics calculation for all requests
  const getStats = () => {
    return {
      total: bonafideRequests.length,
      approved: bonafideRequests.filter((r) => r.status?.toLowerCase() === "approved").length,
      pending: bonafideRequests.filter((r) => r.status?.toLowerCase() === "pending").length,
      rejected: bonafideRequests.filter((r) => r.status?.toLowerCase() === "rejected").length,
    }
  }

  const stats = getStats()

  // Chart data preparation
  const pieData = [
    { name: "Approved", value: stats.approved, color: "#10b981" },
    { name: "Pending", value: stats.pending, color: "#f59e0b" },
    { name: "Rejected", value: stats.rejected, color: "#ef4444" },
  ].filter((item) => item.value > 0)

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading bonafide requests...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: "center", 
            bgcolor: "#f8fafc",
           
            boxShadow: "0.1px 0.8px 1px 0.1px grey"
          }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Requests
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: "center", 
            bgcolor: "#f0fdf4",
          
            boxShadow: "0.1px 0.8px 1px 0.1px grey"
          }}>
            <Typography variant="h4" color="success.main" fontWeight="bold">
              {stats.approved}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Approved
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: "center", 
            bgcolor: "#fffbeb",
           
            boxShadow: "0.1px 0.8px 1px 0.1px grey"
          }}>
            <Typography variant="h4" color="warning.main" fontWeight="bold">
              {stats.pending}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Pending
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 2, 
            textAlign: "center", 
            bgcolor: "#fef2f2",
           
            boxShadow: "0.1px 0.8px 1px 0.1px grey"
          }}>
            <Typography variant="h4" color="error.main" fontWeight="bold">
              {stats.rejected}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Rejected
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Centered Chart Section */}
      <Grid container justifyContent="center" sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "400px", boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: "#1e293b", textAlign: "center" }}>
                Request Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Bonafide Requests Section */}
      <Box>
        <Typography variant="h5" fontWeight="600" sx={{ mb: 3, display: "flex", alignItems: "center", color: "#1e293b" }}>
          <Schedule sx={{ mr: 1, color: "#f59e0b" }} />
          Pending Bonafide Requests ({stats.pending})
        </Typography>

        {/* Search Filter */}
        <Card sx={{ p: 3, mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search pending requests by employee name, ID, purpose, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#f59e0b',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" textAlign="right">
                Showing {filteredRequests.length} of {pendingRequests.length} pending requests
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/* Requests Table */}
        <TableContainer component={Paper} sx={{ boxShadow: "0 2px 12px rgba(0,0,0,0.1)", borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: "#fffbeb" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Purpose</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length > 0 ? (
                paginatedRequests.map((request) => (
                  <TableRow 
                    key={`bonafide-${request.id}`} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: '#fffbeb'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ 
                          mr: 2, 
                          bgcolor: "#fef3c7", 
                          color: "#d97706", 
                          width: 36, 
                          height: 36 
                        }}>
                          <Person sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500} color="#1e293b">
                            {request.employeeName || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {request.employeeId || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">
                        {request.purpose || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">
                        {request.department || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">
                        {request.requestDate 
                          ? new Date(request.requestDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(request.status)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Schedule sx={{ fontSize: 48, color: '#fbbf24', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No pending bonafide requests found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'Try adjusting your search criteria' : 'All requests have been processed'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sx={{
              borderTop: '1px solid #e2e8f0',
              '& .MuiTablePagination-toolbar': {
                minHeight: 64
              }
            }}
          />
        </TableContainer>
      </Box>
    </Box>
  )
}

export default BonafideSection